import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/client/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { Workshop, WorkshopRoleTypes } from '@chancenwerk/shared/workshops/entities/Workshop.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { of, switchMap } from 'rxjs';

/**
 * @customElement akademie-component-workshop-edit
 */
@customElement('akademie-component-workshop-edit')
export class AkademieComponentWorkshopEdit extends ChemistryLitElement {
  @property({ attribute: false }) workshop = new RXController(
    this,
    new Workshop({
      requiresTraining: true,
    }),
  );

  @state() _form = new RXController(
    this,
    this.workshop.observable.pipe(
      switchMap(val => {
        return of(
          new FormController(this, val, {
            onSuccess: async (ent: Workshop) => {
              if (ent._id) {
                await ent.save();
              } else {
                await ent.create();
              }
            },
            error: (err: ValidationError<any>) => {
              this.dispatchEvent(new CustomEvent('error', { detail: err }));
            },
          }),
        );
      }),
    ),
  );

  private async _askForImage() {
    await ChromiumDialog.guard(
      async () => {
        if (!this._form.value?.document) {
          return;
        }

        const file = await askForFile({
          acceptFileTypes: '.jpg,.jpeg,.png,.webp',
          acceptMultipleFiles: false,
        });
        if (!file?.[0]) {
          throw new Error('Beim Upload ist etwas schiefgegangen!');
        }

        this._form.value.document.mediaID = await ImageFile.upload(file[0], {
          acceptFileTypes: '.jpg,.jpeg,.png,.webp',
        });

        this._form.value.document.media = await ImageFile.getByID<ImageFile>(this._form.value.document.mediaID)(
          ImageFile.allFields,
        );

        this.requestUpdate();
      },
      { errorMessagePrefix: 'Fehler beim Hochladen' },
    );
  }

  private async _deleteImage() {
    if (
      !(await ChromiumDialog.confirm('Bild wirklich löschen?', {
        highlightDecline: true,
      }))
    )
      return;

    void ChromiumDialog.guard(async () => {
      if (!this._form.value?.document?.mediaID) return;
      if (!(await ImageFile.removeFile(this._form.value.document.mediaID)())) {
        throw new Error('Das Bild konnte nicht gelöscht werden!');
      }

      this._form.value.document.mediaID = null;
      void ChromiumSnackbar.show('Das Bild wurde gelöscht.');
      this.requestUpdate();
    });
  }

  private _image() {
    if (!this._form.value?.document?.mediaID || !this._form.value.document?.media) {
      return nothing;
    }

    return html`<cr-flex
      horizontal
      padding="sm"
      space="lg"
      ${css({
        justifyContent: 'center',
        alignContent: 'center',
        justifyItems: 'center',
        alignItems: 'center',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: this.sizes.borderRadius,
        width: '240px',
      })}
    >
      <cr-image
        mode="contain"
        .value=${this.workshop.value.mediaID}
        ${css({ height: '150px', width: '150px' })}
      ></cr-image>
      <cr-button mode="icon" tone="error" icon="delete" @click=${async () => this._deleteImage()}></cr-button>
    </cr-flex>`;
  }

  override render() {
    if (!this._form.value) return html``;

    return html` <cr-flex space="md">
      <cr-grid columns="0.6fr 0.4fr max-content max-content" space="md" ${css({ alignItems: 'center' })}>
        <cr-input placeholder="Titel" ${this._form.value.field('title')}> </cr-input>
        <cr-dropdown-selection
          .selectables=${['Optionaler Workshop', 'TtT-Schulungen']}
          .value=${this._form.value.document?.requiresTraining ? 'TtT-Schulungen' : 'Optionaler Workshop'}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            if (!this._form.value?.document) {
              return;
            }
            switch (e.detail.value ?? '') {
              case 'Optionaler Workshop':
                this._form.value.document.requiresTraining = false;
                break;
              case 'TtT-Schulungen':
                this._form.value.document.requiresTraining = true;
                break;
            }
          }}
        ></cr-dropdown-selection>
        <cr-checkbox label="Aktiv" ${this._form.value.field('active')}></cr-checkbox>
        <cr-checkbox label="Anzeige als Einarbeitung" ${this._form.value.field('showAsOnboarding')}></cr-checkbox>
      </cr-grid>
      <cr-text bold ${css({ marginTop: this.spacing.lg })}>Beschreibung</cr-text>
      <x-tinymce
        ${this._form.value.field('description')}
        .editorOptions=${{
          convert_urls: false,
          plugins: allTinyMCEPlugins,
          toolbar:
            'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | link | ltr rtl',
        }}
      ></x-tinymce>
      <cr-text bold ${css({ marginTop: this.spacing.lg })}>Termin-Informationen</cr-text>
      <x-tinymce
        ${this._form.value.field('appointmentInfo')}
        .editorOptions=${{
          convert_urls: false,
          plugins: allTinyMCEPlugins,
          toolbar:
            'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | link | ltr rtl',
        }}
      ></x-tinymce>
      <cr-grid columns="1fr 1fr 1fr" space="md">
        <cr-input placeholder="Zielgruppenerklärung" ${this._form.value.field('audience')}> </cr-input>
        <cr-dropdown-multiselection
          disabled
          flex
          clearable
          placeholder=${'Zielgruppen'}
          icon="hub"
          .selectables=${WorkshopRoleTypes}
          .value=${this._form.value.document?.roles}
          ${this._form.value.field('roles')}
        ></cr-dropdown-multiselection>
      </cr-grid>
      ${this._image()}
      <cr-grid columns="max-content max-content" space="md">
        <cr-button icon="file_upload" ${css({ maxWidth: '250px' })} @click=${async () => this._askForImage()}>
          Bild hochladen
        </cr-button>
        <cr-button icon="save" ${css({ maxWidth: '250px' })} @click=${async () => this._save()}>Speichern</cr-button>
      </cr-grid>
    </cr-flex>`;
  }

  private async _save() {
    const res = await this._form.value?.validate();
    if (res) {
      this.dispatchEvent(new CustomEvent('saved', { detail: { value: res } }));
    }
  }
}
